import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useSearch } from "@tanstack/react-router";
import { Building2, User } from "lucide-react";
import { useTranslation } from "react-i18next";
import { fetchUserOrganizationsQueryOptions } from "@/api/organizations";
import { PageHeader } from "@/components/PageHeader";
import {
	type NavigationItem,
	SideNavigation,
} from "@/components/SideNavigation";
import { useApiParameters } from "@/hooks/useApiParameters";

import { OrganizationsSettings } from "./-components/OrganizationsSettings";
import { ProfileSettings } from "./-components/ProfileSettings";

type SettingsSection = "profile" | "organizations";

export const Route = createFileRoute("/_auth/app/_app/user-settings/")({
	validateSearch: (search: Record<string, unknown>): { tab?: string } => {
		return {
			tab: typeof search.tab === "string" ? search.tab : undefined,
		};
	},
	loader: async ({ context }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);

		// Preload user organizations data
		context.queryClient.ensureQueryData(
			fetchUserOrganizationsQueryOptions(userId, token),
		);

		return { userId, token };
	},
	component: RouteComponent,
});

function RouteComponent() {
	const { t } = useTranslation();
	const { userId, token } = Route.useLoaderData();
	const { tab } = useSearch({ from: "/_auth/app/_app/user-settings/" });

	// Determine active section from URL search params, default to "organizations"
	const activeSection: SettingsSection =
		tab === "profile" ? "profile" : "organizations";

	// Preload organizations data
	useSuspenseQuery(fetchUserOrganizationsQueryOptions(userId, token));

	const navigationItems: NavigationItem[] = [
		{
			key: "profile",
			label: t("userSettings.profile"),
			icon: User,
			to: "/app/user-settings",
			params: {},
		},
		{
			key: "organizations",
			label: t("userSettings.organizations"),
			icon: Building2,
			to: "/app/user-settings",
			params: {},
		},
	];

	// Add search params to navigation items
	const navigationItemsWithSearch: NavigationItem[] = navigationItems.map(
		(item) => ({
			...item,
			to:
				item.key === "organizations"
					? "/app/user-settings"
					: `/app/user-settings?tab=${item.key}`,
		}),
	);

	const renderContent = () => {
		switch (activeSection) {
			case "profile":
				return <ProfileSettings userId={userId} token={token} />;
			case "organizations":
				return <OrganizationsSettings userId={userId} token={token} />;
			default:
				return <OrganizationsSettings userId={userId} token={token} />;
		}
	};

	return (
		<div className="flex flex-col h-full bg-base-200 overflow-hidden">
			{/* Page Header with back button */}
			<div className="p-3 md:p-4 bg-base-100 border-b border-base-300">
				<PageHeader
					title={t("userSettings.title")}
					showBackButton
					backButtonText={t("common.back")}
				/>
			</div>

			<div className="flex flex-row flex-1 overflow-hidden">
				{/* Settings Sidebar */}
				<SideNavigation
					navigationItems={navigationItemsWithSearch}
					activeTab={activeSection}
					testId="user-settings-navigation"
				/>

				{/* Main Content */}
				<main className="flex-1 overflow-auto">{renderContent()}</main>
			</div>
		</div>
	);
}
